import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import { isArticle, isFileFromLatestBatch } from 'screens/ScheduledPosts/utils'
import { isStoryChannel } from 'screens/Calendar/FullScreenPreview/utils'

// Define postTypeFromChannelType FIRST, before any functions that use it
export const postTypeFromChannelType: Record<ChannelType | DesignChannel | GenerationType, string> = {
  [DesignChannel.InstagramPost]: 'Post',
  [DesignChannel.InstagramStory]: 'Story',
  [DesignChannel.FacebookStory]: 'Story',
  [DesignChannel.InstagramReel]: 'Reel',
  [DesignChannel.FacebookPost]: 'Post',
  [DesignChannel.LinkedInPost]: 'Post',
  [DesignChannel.TwitterPost]: 'Post',
  [DesignChannel.TikTokPost]: 'Post',
  [DesignChannel.YoutubeShort]: 'Short',
  [ChannelType.InstagramPosts]: 'Post',
  [ChannelType.LinkedInPosts]: 'Post',
  [ChannelType.Tweets]: 'Post',
  [DesignChannel.GoogleMyBusinessPost]: 'Post',
  [DesignChannel.MetaAds]: '',

  // Covers
  [DesignChannel.TwitterCoverImage]: 'Cover',
  [DesignChannel.YoutubeChannelArt]: 'Cover',
  [DesignChannel.FacebookCover]: 'Cover',
  [DesignChannel.LinkedInBanner]: 'Banner',

  // Profile Pictures
  [DesignChannel.TwitterProfilePicture]: 'Profile',
  [DesignChannel.YoutubeProfilePicture]: 'Profile',
  [DesignChannel.FacebookProfilePicture]: 'Profile',
  [DesignChannel.LinkedInProfilePicture]: 'Profile',

  // Non-postable Posts
  [DesignChannel.WhatsAppSticker]: 'Post',
  [DesignChannel.Pinterest]: 'Post',
  [DesignChannel.Ebay]: 'Post',
  [DesignChannel.Poshmark]: 'Post',
  [DesignChannel.Depop]: 'Post',
  [DesignChannel.Mercari]: 'Post',
  [DesignChannel.MercadoLibre]: 'Post',
  [DesignChannel.Shopee]: 'Post',
  [DesignChannel.Lazada]: 'Post',
  [DesignChannel.Etsy]: 'Post',
  [DesignChannel.Vinted]: 'Post',
  [DesignChannel.Amazon]: 'Post',
  [DesignChannel.Shopify]: 'Post',
  [DesignChannel.Post]: 'Post',

  // Ads
  [ChannelType.BingAds]: 'Ads',
  [ChannelType.AmazonAds]: 'Ads',
  [ChannelType.TikTokAds]: 'Ads',
  [ChannelType.YoutubeAds]: 'Ads',
  [ChannelType.FacebookAds]: 'Ads',
  [ChannelType.GoogleAds]: 'Ads',
  [ChannelType.LinkedInAds]: 'Ads',
  [ChannelType.PinterestAds]: 'Ads',

  // Scripts
  [ChannelType.InstagramReel]: 'Script',
  [ChannelType.TikTokScript]: 'Script',
  [ChannelType.YoutubeScript]: 'Script',

  // Ideas
  [ChannelType.NewsletterIdeas]: 'Ideas',
  [GenerationType.Brainstorm]: 'Ideas',
  [ChannelType.BlogIdeas]: 'Ideas',

  // One-offs
  [DesignChannel.TikTokThumbnail]: 'Thumbnail',
  [DesignChannel.FacebookMarketplace]: 'Marketplace',
  [ChannelType.BlogPost]: 'Blog',
  [ChannelType.InstagramCaptions]: 'Captions',
  [ChannelType.Newsletter]: 'Email',
  [ChannelType.TweetThread]: 'Thread',

  // Docs
  [ChannelType.NurtureEmailSequence]: 'Doc',
  [ChannelType.AbandonedCart]: 'Doc',
  [ChannelType.AboutUs]: 'Doc',
  [ChannelType.BattleCard]: 'Doc',
  [ChannelType.BillingAndPayments]: 'Doc',
  [ChannelType.CallScript]: 'Doc',
  [ChannelType.CandidateNurtureSequence]: 'Doc',
  [ChannelType.CareInstructions]: 'Doc',
  [ChannelType.CaseStudy]: 'Doc',
  [ChannelType.ColdOutboundEmailSequence]: 'Doc',
  [ChannelType.DidntSignUp]: 'Doc',
  [ChannelType.FAQs]: 'Doc',
  [ChannelType.HelpDocs]: 'Doc',
  [ChannelType.Impact]: 'Doc',
  [ChannelType.JobDescription]: 'Doc',
  [ChannelType.LandingPage]: 'Doc',
  [ChannelType.LostCustomer]: 'Doc',
  [ChannelType.Mission]: 'Doc',
  [ChannelType.OnePager]: 'Doc',
  [ChannelType.PaymentConfirmation]: 'Doc',
  [ChannelType.PreCallChecklist]: 'Doc',
  [ChannelType.PressRelease]: 'Doc',
  [ChannelType.Pricing]: 'Doc',
  [ChannelType.ProductDemo]: 'Doc',
  [ChannelType.ProductLaunchAmazon]: 'Doc',
  [ChannelType.ProductLaunchEtsy]: 'Doc',
  [ChannelType.ProductLaunchGoogle]: 'Doc',
  [ChannelType.ProductLaunchShopify]: 'Doc',
  [ChannelType.ProductLaunchWebsite]: 'Doc',
  [ChannelType.PromotionalEmailSequence]: 'Doc',
  [ChannelType.ReplyTemplates]: 'Doc',
  [ChannelType.Research]: 'Doc',
  [ChannelType.ReturnPolicy]: 'Doc',
  [ChannelType.ShippingPolicy]: 'Doc',
  [ChannelType.SMSMessages]: 'Doc',
  [ChannelType.Testimonial]: 'Doc',
  [ChannelType.WarmOutboundEmailSequence]: 'Doc',
  [ChannelType.WelcomeSeries]: 'Doc',
  [GenerationType.ContentPlan]: 'Doc',
  [GenerationType.ContentBrief]: 'Doc',
  [GenerationType.Outline]: 'Doc',

  // Channels that are only used for wizards
  [GenerationType.Article]: 'Doc',
  [GenerationType.Generic]: 'Doc',
  [GenerationType.InlineGeneric]: 'Doc',
  [GenerationType.Campaign]: 'Doc',
  [GenerationType.SEOPlanning]: 'Doc',
  [GenerationType.Image]: 'Doc',
  [GenerationType.InstagramPlanning]: 'Post',
  [GenerationType.TwitterPlanning]: 'Post',
  [GenerationType.FacebookPlanning]: 'Post',
  [GenerationType.LinkedInPlanning]: 'Post',
}

export const getTruncatedText = (text: string, cardWidth: number) => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (!context) return text

  context.font = '12px Inter'

  const measureTextWidth = (text: string) => context.measureText(text).width

  let index = 0
  let currentText = ''

  const windowSizeOffset = window.innerWidth < 1440 ? 20 : 35
  const padding = 25
  const maxWidth = cardWidth - windowSizeOffset - padding

  while (currentText.length < text.length && measureTextWidth(currentText) < maxWidth) {
    currentText += text[index]
    index++
  }

  return currentText
}

export const shouldUseGoHighLevelBrandingForChannelIcon = (externalIntegrations?: ExternalIntegration[]): boolean => {
  // If no external integrations, can't determine Go High Level branding
  if (!externalIntegrations || externalIntegrations.length === 0) {
    return false
  }

  // Check if there's exactly one Go High Level integration
  const ghlIntegrations = externalIntegrations.filter((int) => int.provider === ExternalIntegrationEnum.GoHighLevel)
  return ghlIntegrations.length === 1
}

export const shouldUseGoHighLevelBranding = (
  channel: ChannelType | DesignChannel | GenerationType,
  externalIntegrationId?: number,
  externalIntegrations?: ExternalIntegration[],
): boolean => {
  // Early return if basic conditions aren't met
  if (channel !== ChannelType.BlogPost) {
    return false
  }

  // If no external integrations, can't determine Go High Level branding
  if (!externalIntegrations || externalIntegrations.length === 0) {
    return false
  }

  // If specific integration ID is provided, check that specific integration
  if (externalIntegrationId) {
    const integration = externalIntegrations.find((int) => int.id === externalIntegrationId)
    return integration?.provider === ExternalIntegrationEnum.GoHighLevel
  }

  // Check if there's exactly one Go High Level integration AND no other blog integrations
  const ghlIntegrations = externalIntegrations.filter((int) => int.provider === ExternalIntegrationEnum.GoHighLevel)
  const blogIntegrations = externalIntegrations.filter((int) =>
    SUPPORTED_BLOG_PLATFORMS.includes(int.provider as ExternalIntegrationEnum),
  )

  // Only return true if there's exactly one GHL integration and it's the only blog integration
  return ghlIntegrations.length === 1 && blogIntegrations.length === 1
}

export const shouldUseWordPressBranding = (
  channel: ChannelType | DesignChannel | GenerationType,
  externalIntegrationId?: number,
  externalIntegrations?: ExternalIntegration[],
): boolean => {
  if (channel !== ChannelType.BlogPost) {
    return false
  }

  if (!externalIntegrations || externalIntegrations.length === 0) {
    return false
  }

  if (externalIntegrationId) {
    const integration = externalIntegrations.find((int) => int.id === externalIntegrationId)
    return integration?.provider === ExternalIntegrationEnum.WordPress
  }

  // Check if there's exactly one WordPress integration AND no other blog integrations
  const wpIntegrations = externalIntegrations.filter((int) => int.provider === ExternalIntegrationEnum.WordPress)
  const blogIntegrations = externalIntegrations.filter((int) =>
    SUPPORTED_BLOG_PLATFORMS.includes(int.provider as ExternalIntegrationEnum),
  )

  // Only return true if there's exactly one WP integration and it's the only blog integration
  return wpIntegrations.length === 1 && blogIntegrations.length === 1
}

export const shouldUseWixBranding = (
  channel: ChannelType | DesignChannel | GenerationType,
  externalIntegrationId?: number,
  externalIntegrations?: ExternalIntegration[],
  wixEnabled?: boolean,
): boolean => {
  if (channel !== ChannelType.BlogPost || !wixEnabled) {
    return false
  }

  if (!externalIntegrations || externalIntegrations.length === 0) {
    return false
  }

  if (externalIntegrationId) {
    const integration = externalIntegrations.find((int) => int.id === externalIntegrationId)
    return integration?.provider === ExternalIntegrationEnum.Wix
  }

  // Check if there's exactly one Wix integration AND no other blog integrations
  const wixIntegrations = externalIntegrations.filter((int) => int.provider === ExternalIntegrationEnum.Wix)
  const blogIntegrations = externalIntegrations.filter((int) =>
    SUPPORTED_BLOG_PLATFORMS.includes(int.provider as ExternalIntegrationEnum),
  )

  // Only return true if there's exactly one Wix integration and it's the only blog integration
  return wixIntegrations.length === 1 && blogIntegrations.length === 1
}

export const shouldUseShopifyBranding = (
  channel: ChannelType | DesignChannel | GenerationType,
  externalIntegrationId?: number,
  externalIntegrations?: ExternalIntegration[],
  shopifyEnabled?: boolean,
): boolean => {
  if (channel !== ChannelType.BlogPost || !shopifyEnabled) {
    return false
  }

  if (!externalIntegrations || externalIntegrations.length === 0) {
    return false
  }

  if (externalIntegrationId) {
    const integration = externalIntegrations.find((int) => int.id === externalIntegrationId)
    return integration?.provider === ExternalIntegrationEnum.Shopify
  }

  const shopifyIntegrations = externalIntegrations.filter((int) => int.provider === ExternalIntegrationEnum.Shopify)
  const blogIntegrations = externalIntegrations.filter((int) =>
    SUPPORTED_BLOG_PLATFORMS.includes(int.provider as ExternalIntegrationEnum),
  )

  return shopifyIntegrations.length === 1 && blogIntegrations.length === 1
}

export const shouldUseZapierBranding = (
  channel: ChannelType | DesignChannel | GenerationType,
  externalIntegrationId?: number,
  externalIntegrations?: ExternalIntegration[],
): boolean => {
  if (channel !== ChannelType.BlogPost) {
    return false
  }

  if (!externalIntegrations || externalIntegrations.length === 0) {
    return false
  }

  if (externalIntegrationId) {
    const integration = externalIntegrations.find((int) => int.id === externalIntegrationId)
    return integration?.provider === ExternalIntegrationEnum.Zapier
  }

  // No explicit integration on the file — fall back to "is any Zapier integration
  // flagged as the workspace's blog destination?"
  return externalIntegrations.some(
    (int) =>
      int.provider === ExternalIntegrationEnum.Zapier &&
      (int.additional_data as { is_blog_destination?: boolean } | undefined)?.is_blog_destination === true,
  )
}

export const SUPPORTED_BLOG_PLATFORMS: ExternalIntegrationEnum[] = [
  ExternalIntegrationEnum.WordPress,
  ExternalIntegrationEnum.GoHighLevel,
  ExternalIntegrationEnum.Wix,
  ExternalIntegrationEnum.Shopify,
  // Add future blog platforms here (e.g., Medium, Ghost, etc.)
]

export const isSupportedBlogPlatform = (
  platform: ExternalIntegrationEnum | string,
): platform is ExternalIntegrationEnum => {
  return SUPPORTED_BLOG_PLATFORMS.includes(platform as ExternalIntegrationEnum)
}

export const isBlogPlatformFeatureFlagRequired = (
  platform: ExternalIntegrationEnum,
  wixEnabled?: boolean,
  shopifyEnabled?: boolean,
): boolean => {
  switch (platform) {
    case ExternalIntegrationEnum.GoHighLevel:
      return false
    case ExternalIntegrationEnum.Wix:
      return !wixEnabled
    case ExternalIntegrationEnum.WordPress:
      return false
    case ExternalIntegrationEnum.Shopify:
      return !shopifyEnabled
    default:
      return false // Future blog platforms default to no feature flag required
  }
}

export interface BlogPlatformFeatureFlags {
  wixEnabled?: boolean
  shopifyEnabled?: boolean
  blogPlatformOnboardingBrandingEnabled?: boolean
  // Future blog platform feature flags can be added here
}

export interface BlogPlatformBrandingOptions {
  channel: ChannelType | DesignChannel | GenerationType
  externalIntegrationId?: number
  externalIntegrations?: ExternalIntegration[]
  selectedBlogPlatform?: ExternalIntegrationEnum | string | null
  featureFlags?: BlogPlatformFeatureFlags
}

/**
 * Enhanced blog branding logic that determines which blog platform branding to show
 * based on connected integrations and user's onboarding selection.
 * PRIORITY 1: Connected Integration
 * PRIORITY 2: Onboarding Selection
 * PRIORITY 3: Generic
 */
export const getBlogPlatformBrandingWithOnboardingSelection = ({
  channel,
  externalIntegrationId,
  externalIntegrations,
  selectedBlogPlatform,
  featureFlags,
}: BlogPlatformBrandingOptions): ExternalIntegrationEnum | null => {
  if (channel !== ChannelType.BlogPost) {
    return null
  }

  // Extract feature flags with smart defaults
  const wixEnabled = featureFlags?.wixEnabled ?? false
  const shopifyEnabled = featureFlags?.shopifyEnabled ?? false
  const blogPlatformOnboardingBrandingEnabled = featureFlags?.blogPlatformOnboardingBrandingEnabled ?? false

  const useGHL = shouldUseGoHighLevelBranding(channel, externalIntegrationId, externalIntegrations)
  const useWordPress = shouldUseWordPressBranding(channel, externalIntegrationId, externalIntegrations)
  const useWix = shouldUseWixBranding(channel, externalIntegrationId, externalIntegrations, wixEnabled)
  const useShopify = shouldUseShopifyBranding(channel, externalIntegrationId, externalIntegrations, shopifyEnabled)
  const useZapier = shouldUseZapierBranding(channel, externalIntegrationId, externalIntegrations)

  // Feature flag disabled: Use ONLY original logic, ignore onboarding selection
  if (!blogPlatformOnboardingBrandingEnabled) {
    if (useGHL) {
      return ExternalIntegrationEnum.GoHighLevel
    } else if (useWordPress) {
      return ExternalIntegrationEnum.WordPress
    } else if (useWix) {
      return ExternalIntegrationEnum.Wix
    } else if (useShopify) {
      return ExternalIntegrationEnum.Shopify
    } else if (useZapier) {
      return ExternalIntegrationEnum.Zapier
    }
    return null
  }

  // Feature flag enabled: Use priority system
  // PRIORITY 1: Connected Integration
  if (externalIntegrations && externalIntegrations.length > 0) {
    if (useGHL) {
      return ExternalIntegrationEnum.GoHighLevel
    } else if (useWordPress) {
      return ExternalIntegrationEnum.WordPress
    } else if (useWix) {
      return ExternalIntegrationEnum.Wix
    } else if (useShopify) {
      return ExternalIntegrationEnum.Shopify
    } else if (useZapier) {
      return ExternalIntegrationEnum.Zapier
    }
    return null
  }

  // PRIORITY 2: Onboarding Selection (only if no integrations)
  if (selectedBlogPlatform === ExternalIntegrationEnum.Zapier) {
    return ExternalIntegrationEnum.Zapier
  }
  if (selectedBlogPlatform && isSupportedBlogPlatform(selectedBlogPlatform)) {
    if (!isBlogPlatformFeatureFlagRequired(selectedBlogPlatform, wixEnabled, shopifyEnabled)) {
      return selectedBlogPlatform
    }
  }

  // PRIORITY 3: Generic fallback
  return null
}

export const getContentTypeLabel = (channel: ChannelType | DesignChannel | GenerationType): string => {
  if (channel === ChannelType.BlogPost) {
    return 'Blog'
  }

  return postTypeFromChannelType[channel]
}

export const SHORT_FORM_VIDEO_CHANNELS: DesignChannel[] = [
  DesignChannel.YoutubeShort,
  DesignChannel.TikTokPost,
  DesignChannel.InstagramReel,
]

/** Item shape needed to resolve label (e.g. AlmanacFileIndexItem or AlmanacFileCalendarEvent). */
export type ResolvedLabelItem = {
  scheduled_post_targets?: Array<{ provider?: string | null }> | null
  channel?: ChannelType | DesignChannel | null
  is_carousel: boolean
}

/**
 * Resolved label: target-based when there are targets, otherwise channel-based.
 * Extracts targetsWithProviders and channel from the item so callers pass only the item.
 * When no channel and no targets, returns 'Post'
 */
export function getResolvedLabel(item: ResolvedLabelItem): string {
  if (item.is_carousel) {
    return 'Carousel'
  }

  const targetsWithProviders = (item.scheduled_post_targets ?? []).filter((t) => !!t.provider)
  const channel = item?.channel

  if (targetsWithProviders.length === 0) {
    return channel ? getContentTypeLabel(channel as ChannelType | DesignChannel) : 'Post'
  }
  if (targetsWithProviders.length > 1) {
    if (isStoryChannel(channel as DesignChannel)) return 'Story'
    return 'Post'
  }

  const target = targetsWithProviders[0]

  if (channel != null && SHORT_FORM_VIDEO_CHANNELS.includes(channel as DesignChannel)) {
    return (
      {
        [ExternalIntegrationEnum.YouTube]: 'Short',
        [ExternalIntegrationEnum.Instagram]: 'Reel',
      }[target.provider ?? ''] ?? 'Post'
    )
  }
  return channel ? getContentTypeLabel(channel as ChannelType | DesignChannel) : 'Post'
}

type EventCardPostProperties = {
  isArticlePreview: boolean
  isLoading: boolean
  isLoadingDocPreview: boolean
  isWithinPreviewImageWaitTime: boolean
  showDocText: boolean
}

export const getEventProperties = (
  event: AlmanacFileCalendarEvent,
  imageLoaded: boolean,
  designLoaded: boolean,
  isCopilot: boolean,
  calendarV2: boolean = false,
): EventCardPostProperties => {
  const isArticlePreview = !!isArticle({ almanac_file: event })
  const isWithinPreviewImageWaitTime = isFileFromLatestBatch(event)

  const getIsEventLoading = () => {
    if (calendarV2) return !!event.is_loading
    if (event.image_url && imageLoaded) return false
    if (event.design_content && designLoaded) return false
    if (isArticlePreview && (!!event.image_url || (!!event.content && !isWithinPreviewImageWaitTime))) return false
    return true
  }

  const showDocText =
    (!!event.content && isArticlePreview && !event.image_url && !event.empty && !isWithinPreviewImageWaitTime) ||
    (!!event.is_design_blank && isCopilot)

  return {
    isArticlePreview,
    isLoading: getIsEventLoading(),
    isLoadingDocPreview: isArticlePreview && !event.image_url && isWithinPreviewImageWaitTime,
    isWithinPreviewImageWaitTime,
    showDocText,
  }
}
